import {
  Badge,
  Flex,
  IconButton,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon, ViewIcon } from "@chakra-ui/icons";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  CANAL_LABEL,
  CatalogoDisparo,
  DisparoListItem,
  PROVEDOR_LABEL,
  TIPO_GATILHO_LABEL,
  TipoGatilho,
  corContexto,
  descreverGatilho,
  rotuloContexto,
} from "@/models/consignado/disparo.model";
import {
  EDGE_COLUMN_PX,
  tableCellStyles,
  tableHeaderStyles,
} from "@/components/FinancialShared/tableStyles";
import { evento as buscarEvento } from "./catalogo.helpers";

type Props = {
  disparos: DisparoListItem[];
  catalogo?: CatalogoDisparo;
  togglingId?: string | null;
  onView: (disparo: DisparoListItem) => void;
  onEdit: (disparo: DisparoListItem) => void;
  onDelete: (disparo: DisparoListItem) => void;
  onToggleAtivo: (disparo: DisparoListItem) => void;
};

// Mesmos estilos da tabela de Progresso leilão (FinancialShared/tableStyles).
const headerStyles = { ...tableHeaderStyles, fontSize: "10px" };
const cellStyles = { ...tableCellStyles, fontSize: "12px" };

// Grade do GET /disparos — a resposta da lista é enxuta de propósito
// (nome, ativo, contexto, gatilho, canais); filtro, template e auditoria só
// vêm no GET por id, então não há colunas pra eles aqui.
export default function DisparoTable({
  disparos,
  catalogo,
  togglingId,
  onView,
  onEdit,
  onDelete,
  onToggleAtivo,
}: Props) {
  const backgroundStyle = useConditionalBackground();

  if (disparos.length === 0) {
    return (
      <Flex w="100%" justify="center" py={12}>
        <Text color="gray.500" fontFamily="Lexend Deca">
          Nenhum disparo cadastrado. Clique em "Novo disparo" para criar o primeiro.
        </Text>
      </Flex>
    );
  }

  return (
    <TableContainer borderRadius="lg" overflowX="auto">
      <Table variant="simple">
        <Thead {...backgroundStyle}>
          <Tr>
            <Th {...headerStyles} px={EDGE_COLUMN_PX} width="130px">AÇÕES</Th>
            <Th {...headerStyles} width="80px">ATIVO</Th>
            <Th {...headerStyles} width="220px">NOME</Th>
            <Th {...headerStyles} width="140px">CONTEXTO</Th>
            <Th {...headerStyles} width="240px">GATILHO</Th>
            <Th {...headerStyles} px={EDGE_COLUMN_PX} width="200px">CANAL</Th>
          </Tr>
        </Thead>
        <Tbody>
          {disparos.map((disparo) => {
            const ev = buscarEvento(catalogo, disparo.gatilho.evento);
            return (
              <Tr key={disparo.id} _odd={{ bg: "blackAlpha.50" }} _hover={{ bg: "gray.100" }}>
                <Td {...cellStyles} px={EDGE_COLUMN_PX}>
                  <Flex gap={1} justify="center">
                    <Tooltip label="Visualizar" placement="top">
                      <IconButton
                        aria-label="Visualizar disparo"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => onView(disparo)}
                      />
                    </Tooltip>
                    <Tooltip label="Editar" placement="top">
                      <IconButton
                        aria-label="Editar disparo"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="gray"
                        onClick={() => onEdit(disparo)}
                      />
                    </Tooltip>
                    <Tooltip label="Excluir" placement="top">
                      <IconButton
                        aria-label="Excluir disparo"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => onDelete(disparo)}
                      />
                    </Tooltip>
                  </Flex>
                </Td>
                <Td {...cellStyles}>
                  <Switch
                    isChecked={disparo.ativo}
                    colorScheme="green"
                    isDisabled={togglingId === disparo.id}
                    onChange={() => onToggleAtivo(disparo)}
                  />
                </Td>
                <Td {...cellStyles}>
                  <Text whiteSpace="normal" wordBreak="break-word" lineHeight="1.3" fontFamily="Lexend Deca">
                    {disparo.nome}
                  </Text>
                </Td>
                <Td {...cellStyles}>
                  <Badge
                    colorScheme={corContexto(disparo.contexto)}
                    borderRadius="md"
                    px={2}
                    py={1}
                    fontFamily="Lexend Deca"
                    fontSize="10px"
                    fontWeight="normal"
                  >
                    {rotuloContexto(disparo.contexto, catalogo)}
                  </Badge>
                </Td>
                <Td {...cellStyles}>
                  <Flex direction="column" gap={1} align="center">
                    <Text
                      fontSize="12px"
                      whiteSpace="normal"
                      wordBreak="break-word"
                      lineHeight="1.3"
                      fontFamily="Lexend Deca"
                    >
                      {disparo.gatilho.tipo === TipoGatilho.EVENTO && ev
                        ? ev.descricao
                        : descreverGatilho(disparo.gatilho)}
                    </Text>
                    <Text fontSize="10px" color="gray.500" fontFamily="Lexend Deca">
                      {TIPO_GATILHO_LABEL[disparo.gatilho.tipo]}
                    </Text>
                  </Flex>
                </Td>
                <Td {...cellStyles} px={EDGE_COLUMN_PX}>
                  <Wrap spacing={1} justify="center">
                    {disparo.canais.map((c, i) => (
                      <WrapItem key={`${c.canal}-${i}`}>
                        <Badge
                          colorScheme="green"
                          borderRadius="md"
                          px={2}
                          py={1}
                          fontFamily="Lexend Deca"
                          fontSize="10px"
                          fontWeight="normal"
                        >
                          {CANAL_LABEL[c.canal]} · {PROVEDOR_LABEL[c.provedor]}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
